/* eslint-disable react/react-in-jsx-scope */
import { useModals } from '@/hooks/ModalManager';
import {
  Alert,
  Box,
  Button,
  chakra,
  Flex,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type DeleteConfirmationProps = {
  description?: React.ReactNode;
  itemName: string;
  buttonLabel: React.ReactNode;
  onConfirm: () => void;
};

function DeleteConfirmation({
  buttonLabel,
  itemName,
  onConfirm,
  onClose,
  description,
}: DeleteConfirmationProps & { onClose: () => void }) {
  const [confirmation, setConfirmation] = React.useState('');
  const { t } = useTranslation();

  return (
    <Flex flexDir="column" w="full">
      <Alert
        p="3"
        bg="yellow.300"
        rounded="none"
        status="warning"
        variant="subtle"
        fontSize="lg"
        borderTopColor="yellow.400"
        borderBottomColor="yellow.400"
        borderTopWidth="1.5px"
        borderBottomWidth="1.5px"
      >
        {t('delete_confirmation.warning')}
      </Alert>
      <VStack spacing="3" w="full" p="4" textAlign="start">
        <Box color="mutedText" w="full" textAlign="start" fontSize="lg">
          <chakra.div>
            <chakra.span>{description}</chakra.span>
          </chakra.div>
          <chakra.div mt="4">
            <chakra.span>{t('delete_confirmation.type')}</chakra.span>
            <chakra.span display="inline" fontWeight="bold">
              {`\n ${itemName} \n`}
            </chakra.span>
            <chakra.span>{t('delete_confirmation.to_confirm')}</chakra.span>
          </chakra.div>
        </Box>

        <VStack w="full" spacing="4">
          <Input
            size="sm"
            rounded="md"
            onChange={(e) => setConfirmation(e.target.value)}
          />
          <Button
            w="full"
            variant="subtle"
            color="red"
            border="1px"
            borderColor="gray.300"
            disabled={confirmation !== itemName}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {buttonLabel}
          </Button>
        </VStack>
      </VStack>
    </Flex>
  );
}

export function useDeleteConfirmation() {
  const { t } = useTranslation();
  const modals = useModals();
  return (props: DeleteConfirmationProps) => {
    const onClose = () => modals.close('confirm-delete');
    modals.open({
      id: 'confirm-delete',
      title: t('forms.absolutely_sure'),
      size: 'lg',
      children: <DeleteConfirmation onClose={onClose} {...props} />,
    });
  };
}
